<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { MODAL_TYPES, QnaEditModalProps, useModalStore } from '@/stores/modal';
import client from '@/api/graphql/client';

const FIELD =
  'w-full h-[44px] border border-[#E6E6E6] rounded-[6px] px-3 text-[16px] bg-white text-[#404040] placeholder-[#404040] font-[400] focus:outline-none focus:ring-0';
const TEXTAREA =
  'w-full min-h-[120px] border border-[#E6E6E6] rounded-[6px] px-[12px] py-[15px] text-[16px] bg-white text-[#404040] font-[400] placeholder-[#404040] focus:outline-none focus:ring-0 tracking-[-0.3px]';

const props = defineProps<QnaEditModalProps>();
const modalStore = useModalStore();

type AttachedFileItem = {
  file: File;
  previewUrl: string;
};

const state = reactive({
  category: props.item.major_cat || '',
  inquiryType: props.item.sub_cat || '',
  textareaValue: '',
  addrKeyword: '',
  addrZip: '',
  addrBase: '',
  addrRef: '',
  addrDetail: '',
  changeReason: '',
  attachedFiles: [] as AttachedFileItem[],
  errorMsg: '',
  isSubmitting: false,
});

const fileInput = ref<HTMLInputElement | null>(null);
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_FILES = 1;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];

const existingPreviewMap = ref<Record<number, string>>({});
const pendingExistingPreviews = new Set<number>();

// 초기 주소 값 저장 (변경사항 감지용)
const initialAddressValues = ref<{
  addrZip: string;
  addrBase: string;
  addrDetail: string;
  changeReason: string;
  textareaValue: string;
}>({
  addrZip: '',
  addrBase: '',
  addrDetail: '',
  changeReason: '',
  textareaValue: '',
});

const hasSub = computed(() => (props.inquiryTypesMap[state.category]?.length || 0) > 0);
const inquiryTypes = computed(() => props.inquiryTypesMap[state.category] || []);

const showFileAttachment = computed(() => {
  return (
    state.category === '시스템' ||
    state.category === '내정보' ||
    state.category === '회원활동정보' ||
    (props.item.files && props.item.files.length > 0)
  );
});

const cleanupExistingPreviews = () => {
  Object.values(existingPreviewMap.value).forEach(url => URL.revokeObjectURL(url));
  existingPreviewMap.value = {};
  pendingExistingPreviews.clear();
};

const ensureExistingPreview = async (fileId: number) => {
  if (existingPreviewMap.value[fileId] || pendingExistingPreviews.has(fileId)) return;

  pendingExistingPreviews.add(fileId);

  try {
    const response = await client.get(`/api/qna/files/${fileId}`, {
      responseType: 'blob',
      headers: {
        Accept: 'image/*',
      },
    });

    const blob = response.data as Blob;
    const previewUrl = URL.createObjectURL(blob);
    existingPreviewMap.value = {
      ...existingPreviewMap.value,
      [fileId]: previewUrl,
    };
  } catch (error) {
    console.error('❌ 이미지 로딩 실패:', fileId, error);
  } finally {
    pendingExistingPreviews.delete(fileId);
  }
};

const loadExistingFiles = () => {
  cleanupExistingPreviews();
  (props.item.files || []).forEach(file => ensureExistingPreview(file.file_id));
};

const openImageViewer = async (fileId: number, filename: string) => {
  await ensureExistingPreview(fileId);
  const imageUrl = existingPreviewMap.value[fileId];
  if (!imageUrl) {
    modalStore.open(MODAL_TYPES.SIMPLE_ALERT, { message: '파일을 찾을 수 없습니다' });
    return;
  }
  modalStore.open(MODAL_TYPES.IMAGE_VIEWER, {
    src: imageUrl,
    filename: filename,
    alt: filename,
  });
};

const openNewImageViewer = (previewUrl: string, filename: string) => {
  modalStore.open(MODAL_TYPES.IMAGE_VIEWER, {
    src: previewUrl,
    filename: filename,
    alt: filename,
  });
};

const initializeForm = () => {
  state.attachedFiles.forEach(item => URL.revokeObjectURL(item.previewUrl));
  state.category = props.item.major_cat || '';
  state.inquiryType = props.item.sub_cat || '';
  state.attachedFiles = [];
  state.errorMsg = '';

  const content = props.item.inquiry_content?.replace(/\\n/g, '\n') || '';
  if (state.inquiryType === '주소') {
    const lines = content.split('\n');
    state.addrZip = '';
    state.addrBase = '';
    state.addrRef = '';
    state.addrDetail = '';
    state.changeReason = '';
    lines.forEach(line => {
      if (line.startsWith('우편번호:')) state.addrZip = line.replace('우편번호:', '').trim();
      else if (line.startsWith('주소:')) state.addrBase = line.replace('주소:', '').trim();
      else if (line.startsWith('참고항목:')) state.addrRef = line.replace('참고항목:', '').trim();
      else if (line.startsWith('상세주소:')) state.addrDetail = line.replace('상세주소:', '').trim();
      else if (line.startsWith('사유:')) state.changeReason = line.replace('사유:', '').trim();
    });
    state.addrKeyword = state.addrBase;
    state.textareaValue = '';

    // 초기 주소 값 저장
    initialAddressValues.value = {
      addrZip: state.addrZip,
      addrBase: state.addrBase,
      addrDetail: state.addrDetail,
      changeReason: state.changeReason,
      textareaValue: '',
    };
  } else {
    state.textareaValue = content;
    state.addrKeyword = '';
    state.addrZip = '';
    state.addrBase = '';
    state.addrRef = '';
    state.addrDetail = '';
    state.changeReason = '';

    // 초기 텍스트 값 저장
    initialAddressValues.value = {
      addrZip: '',
      addrBase: '',
      addrDetail: '',
      changeReason: '',
      textareaValue: content,
    };
  }

  loadExistingFiles();
};

watch(
  () => props.item,
  () => initializeForm(),
  { immediate: true },
);

watch(
  () => props.item.files,
  () => loadExistingFiles(),
  { deep: true },
);

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const files = Array.from(target.files || []);

  for (const file of files) {
    if (state.attachedFiles.length >= MAX_FILES) {
      alert(`최대 ${MAX_FILES}개의 파일만 첨부할 수 있습니다.`);
      break;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(`${file.name}은(는) 20MB를 초과합니다.`);
      continue;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      alert(`${file.name}은(는) 허용되지 않는 파일 형식입니다.\n허용 형식: ${ALLOWED_EXTENSIONS.join(', ')}`);
      continue;
    }

    if (state.attachedFiles.some(f => f.file.name === file.name && f.file.size === file.size)) {
      alert(`${file.name}은(는) 이미 첨부되어 있습니다.`);
      continue;
    }

    const previewUrl = URL.createObjectURL(file);
    state.attachedFiles.push({ file, previewUrl });
    cleanupExistingPreviews();
    existingPreviewMap.value = {};
  }

  if (target) target.value = '';
};

const removeFile = (index: number) => {
  const [removed] = state.attachedFiles.splice(index, 1);
  if (removed) {
    URL.revokeObjectURL(removed.previewUrl);
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// 주소 필드 변경사항 확인
const hasAddressChanges = computed(() => {
  if (state.inquiryType !== '주소') return false;
  return (
    state.addrZip.trim() !== initialAddressValues.value.addrZip.trim() ||
    state.addrBase.trim() !== initialAddressValues.value.addrBase.trim() ||
    state.addrDetail.trim() !== initialAddressValues.value.addrDetail.trim() ||
    state.changeReason.trim() !== initialAddressValues.value.changeReason.trim()
  );
});

// 텍스트 필드 변경사항 확인
const hasTextChanges = computed(() => {
  if (state.inquiryType === '주소') return false;
  return state.textareaValue.trim() !== initialAddressValues.value.textareaValue.trim();
});

// 파일 변경사항 확인
const hasFileChanges = computed(() => {
  return state.attachedFiles.length > 0;
});

const isSubmitEnabled = computed(() => {
  if (!state.category) return false;
  if (hasSub.value) {
    if (!state.inquiryType) return false;
    if (state.inquiryType === '주소') {
      // 필수 필드가 모두 채워져 있고, 변경사항이 있어야 함
      const allFieldsFilled = !!(
        state.addrZip.trim() &&
        state.addrBase.trim() &&
        state.addrDetail.trim() &&
        state.changeReason.trim()
      );
      return allFieldsFilled && (hasAddressChanges.value || hasFileChanges.value);
    }
    // 텍스트 필드의 경우 변경사항이 있거나 파일이 변경되었을 때만 활성화
    const hasContent = state.textareaValue.trim().length > 0;
    return hasContent && (hasTextChanges.value || hasFileChanges.value);
  }
  // 텍스트 필드의 경우 변경사항이 있거나 파일이 변경되었을 때만 활성화
  const hasContent = state.textareaValue.trim().length > 0;
  return hasContent && (hasTextChanges.value || hasFileChanges.value);
});

const buildMessage = () => {
  if (state.inquiryType === '주소') {
    return `우편번호: ${state.addrZip}\n주소: ${state.addrBase}\n참고항목: ${state.addrRef}\n상세주소: ${state.addrDetail}\n사유: ${state.changeReason}`;
  }
  return state.textareaValue;
};

const handleSubmit = async () => {
  if (!isSubmitEnabled.value) return;

  state.isSubmitting = true;
  state.errorMsg = '';

  try {
    const result = await props.onSubmit({
      boardId: props.item.board_id,
      majorCat: state.category,
      subCat: state.inquiryType || undefined,
      message: buildMessage(),
      file: state.attachedFiles[0]?.file || null,
    });

    if (result.success) {
      state.attachedFiles.forEach(item => URL.revokeObjectURL(item.previewUrl));
      state.attachedFiles = [];
      cleanupExistingPreviews();
      modalStore.close(MODAL_TYPES.QNA_EDIT);
    } else {
      state.errorMsg = result.error || '문의 수정 중 오류가 발생했습니다.';
    }
  } catch (error) {
    state.errorMsg = error instanceof Error ? error.message : '문의 수정 중 오류가 발생했습니다.';
  } finally {
    state.isSubmitting = false;
  }
};

const handleCancel = () => {
  state.attachedFiles.forEach(item => URL.revokeObjectURL(item.previewUrl));
  state.attachedFiles = [];
  cleanupExistingPreviews();
  modalStore.close(MODAL_TYPES.QNA_EDIT);
};
</script>

<template>
  <BaseModal title="문의 수정" width="500px" wrapper-class="qna-edit-modal" @close="handleCancel">
    <div class="modal-body">
      <div class="form-section">
        <div class="form-scroll">
          <div class="form-group">
            <label for="edit-category" class="block text-[13px] text-[#757575] mb-1">구분</label>
            <div class="relative w-full h-[44px]">
              <select
                id="edit-category"
                v-model="state.category"
                :class="
                  FIELD + ' appearance-none pr-14 block h-full w-full cursor-not-allowed bg-[#F5F5F5] text-[#BDBDBD]'
                "
                disabled
              >
                <option value="">선택해주세요</option>
                <option v-for="cat in props.categories" :key="cat.value" :value="cat.value">{{ cat.text }}</option>
              </select>
              <span
                class="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 flex items-center text-[#9CA3AF] z-10"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="block"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </div>
          </div>

          <div class="form-group" v-if="hasSub">
            <label for="edit-sub" class="block text-[13px] text-[#666666] mb-1">세부항목</label>
            <div class="relative w-full h-[44px]">
              <select
                id="edit-sub"
                v-model="state.inquiryType"
                :class="
                  FIELD + ' appearance-none pr-14 block h-full w-full cursor-not-allowed bg-[#F5F5F5] text-[#BDBDBD]'
                "
                disabled
              >
                <option value="">세부항목 선택</option>
                <option v-for="type in inquiryTypes" :key="type.value" :value="type.value">{{ type.text }}</option>
              </select>
              <span
                class="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 flex items-center text-[#9CA3AF] z-10"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="block"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </div>
          </div>

          <template v-if="state.inquiryType === '주소'">
            <div class="form-group">
              <label class="block text-[13px] text-[#666666] mb-1"
                >주소 검색 <span class="text-primary-500">*</span></label
              >
              <div class="relative">
                <input
                  :class="[
                    FIELD,
                    'cursor-not-allowed bg-[#F5F5F5] text-[#BDBDBD]',
                    !state.addrZip.trim() && !isSubmitEnabled ? 'border-primary-500' : '',
                  ]"
                  v-model="state.addrKeyword"
                  placeholder="예) 판교역로 166"
                  aria-label="주소 검색"
                  disabled
                />
              </div>
            </div>
            <div class="form-group">
              <label class="block text-[13px] text-[#666666] mb-1"
                >우편번호 <span class="text-primary-500">*</span></label
              >
              <input
                :class="[FIELD, !state.addrZip.trim() && !isSubmitEnabled ? 'border-primary-500' : '']"
                v-model="state.addrZip"
                placeholder="주소 검색을 통해 입력됩니다"
                readonly
              />
            </div>
            <div class="form-group">
              <label class="block text-[13px] text-[#666666] mb-1">주소 <span class="text-primary-500">*</span></label>
              <input
                :class="[FIELD, !state.addrBase.trim() && !isSubmitEnabled ? 'border-primary-500' : '']"
                v-model="state.addrBase"
                placeholder="주소 검색을 통해 입력됩니다"
                readonly
              />
            </div>
            <div class="form-group">
              <label class="block text-[13px] text-[#666666] mb-1"
                >상세주소 <span class="text-primary-500">*</span></label
              >
              <input
                :class="[FIELD, !state.addrDetail.trim() && !isSubmitEnabled ? 'border-primary-500' : '']"
                v-model="state.addrDetail"
                placeholder="상세주소를 입력하세요"
                :aria-invalid="!state.addrDetail.trim()"
              />
            </div>
            <div class="form-group relative">
              <label class="block text-[13px] text-[#666666] mb-1"
                >주소 변경 사유 <span class="text-primary-500">*</span></label
              >
              <textarea
                :class="[TEXTAREA, !state.changeReason.trim() && !isSubmitEnabled ? 'border-primary-500' : '']"
                v-model="state.changeReason"
                maxlength="500"
                placeholder="주소 변경 사유를 써주세요."
              ></textarea>
              <div class="absolute right-1 bottom-1 text-[11px] text-[#9CA3AF]">
                {{ state.changeReason.length }}/500자
              </div>
            </div>
          </template>

          <template v-else>
            <div class="form-group relative">
              <label for="edit-content" class="block text-[13px] text-[#666666] mb-1">문의내용</label>
              <textarea
                id="edit-content"
                v-model="state.textareaValue"
                :class="TEXTAREA + ' pr-16'"
                maxlength="500"
                placeholder="궁금한 내용을 500자 이내로 작성해주세요."
              ></textarea>
              <div class="char-counter">{{ state.textareaValue.length }}/500자</div>
            </div>
          </template>

          <div v-if="showFileAttachment" class="form-group">
            <label class="block text-[13px] text-[#666666] mb-2">파일 첨부 (선택)</label>

            <button
              v-if="state.category === '시스템' || state.category === '내정보' || state.category === '회원활동정보'"
              type="button"
              @click="fileInput?.click()"
              class="w-full h-[44px] border border-[#E6E6E6] rounded-[6px] px-3 bg-white text-[#757575] text-[14px] font-[400] flex items-center justify-center gap-2 hover:bg-[#F9FAFB] transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                />
              </svg>
              <span>이미지 첨부하기 (최대 {{ MAX_FILES }}개, 20MB 이하)</span>
            </button>

            <input
              ref="fileInput"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
              @change="event => handleFileSelect(event as unknown as Event)"
              class="hidden"
            />

            <div v-if="!state.attachedFiles.length && props.item.files && props.item.files.length > 0" class="mt-3">
              <p class="text-[13px] text-[#666666] font-semibold mb-2">현재 적용된 이미지</p>
              <div v-for="file in props.item.files" :key="file.file_id" class="mb-2">
                <button
                  type="button"
                  class="text-[11px] text-[#9CA3AF] mt-1 underline decoration-dotted hover:text-[#757575]"
                  @click="openImageViewer(file.file_id, file.original_filename)"
                  aria-label="이미지 파일 보기"
                >
                  {{ file.original_filename }} ({{ formatFileSize(file.file_size) }})
                </button>
              </div>
            </div>

            <div v-if="state.attachedFiles.length > 0" class="mt-3">
              <div v-for="(item, index) in state.attachedFiles" :key="item.previewUrl" class="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  class="text-[11px] text-[#9CA3AF] mt-1 underline decoration-dotted hover:text-[#757575]"
                  @click="openNewImageViewer(item.previewUrl, item.file.name)"
                  aria-label="이미지 파일 보기"
                >
                  {{ item.file.name }} ({{ formatFileSize(item.file.size) }})
                </button>
                <button
                  type="button"
                  @click="removeFile(index)"
                  class="text-[#9CA3AF] hover:text-primary-500 transition-colors"
                  title="파일 삭제"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="mt-2 text-[11px] text-[#9CA3AF]">
              허용 형식: {{ ALLOWED_EXTENSIONS.join(', ').toUpperCase() }}
            </div>
          </div>

          <div v-if="state.errorMsg" class="text-[13px] text-primary-500">
            {{ state.errorMsg }}
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <ModalFooterConfirmButtons
        :confirm-text="'수정하기'"
        cancel-text="취소"
        :disabled="!isSubmitEnabled || state.isSubmitting"
        :is-loading="state.isSubmitting"
        @confirm="handleSubmit"
        @cancel="handleCancel"
      />
    </template>
  </BaseModal>
</template>

<style scoped>
:global(.qna-edit-modal) {
  padding: 0 24px 24px 24px;
  gap: 32px;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 16px;
  padding-bottom: 16px;
}

.form-section {
  display: flex;
  flex-direction: column;
  /* gap: 40px; */
}

.form-scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: calc(80vh - 200px); /* 모달 전체 높이에서 헤더/푸터 영역 제외 */
  overflow-y: auto; /* 스크롤 활성화 */
  padding-right: 8px;

  /* 모바일에서는 더 작은 높이 */
  @media (max-width: 767px) {
    max-height: calc(70vh - 180px);
  }
}

.form-group {
  width: 100%;
}

.char-counter {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 6px;
  font-size: 11px;
  color: #757575;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
}
</style>
